import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tickets } from '../api/methods';
import { keepNewerTicket } from '../utils/ticketRevision';
import { getSessionEpoch, isCurrentSessionEpoch, StaleSessionError } from '../utils/sessionEpoch';
import { siteKeys } from './useSites';
import { reviewKeys } from './useReviewQueue';
import { dashboardKeys } from './useDashboard';
import { teamKeys } from './useTeam';
/**
 * Delay before re-fetching a ticket to pick up the result of the server-side
 * Trello sync (which runs async after the mutation response returns).
 *
 * Derivation: ~1s debounce + ~500ms Trello API call + ~1s retry buffer ≈ 2.5s
 * minimum. 6s gives a safe margin for slow networks and transient retries.
 */
const TRELLO_SYNC_FAILURE_POLL_MS = 6000;
/**
 * Module-level registry of pending Trello-sync-failure poll timeouts, keyed by
 * ticket id. Allows `useSyncTrello` (the retry action) to cancel a pending
 * delayed invalidation scheduled by `useUpdateTicket`, preventing a stale
 * post-retry refetch from clobbering fresh state. Also lets unmount cleanup
 * reach the latest scheduled handle even if the mutation is triggered outside
 * the hook's component tree.
 */
const pendingTrelloSyncInvalidations = new Map();
function cancelPendingTrelloSyncInvalidation(ticketId) {
    const handle = pendingTrelloSyncInvalidations.get(ticketId);
    if (handle !== undefined) {
        clearTimeout(handle);
        pendingTrelloSyncInvalidations.delete(ticketId);
    }
}
export const ticketKeys = {
    all: ['tickets'],
    list: (cardId) => ['tickets', 'list', cardId],
    active: ['tickets', 'active'],
    activeFiltered: (filters) => [
        'tickets',
        'active',
        {
            statuses: filters.statuses ? [...filters.statuses].sort() : undefined,
            boardId: filters.boardId || undefined,
            includeImports: filters.includeImports ?? false,
            intermediaryAwaitingPayment: filters.intermediaryAwaitingPayment ?? false,
        },
    ],
    detail: (ticketId) => ['tickets', 'detail', ticketId],
    auditTrail: (ticketId) => ['tickets', 'detail', ticketId, 'audit'],
    attachments: (ticketId) => ['tickets', 'attachments', ticketId],
    // Nested under `detail` so invalidating a ticket's detail cascades to its comments.
    comments: (ticketId) => ['tickets', 'detail', ticketId, 'comments'],
    qboSyncCandidates: ['tickets', 'qbo-sync', 'candidates'],
    qboSyncOperation: (operationId) => ['tickets', 'qbo-sync', 'operations', operationId],
};
function invalidateTicketRollups(queryClient, siteId, invalidateAllSites = false) {
    queryClient.invalidateQueries({ queryKey: ticketKeys.active });
    queryClient.invalidateQueries({ queryKey: dashboardKeys.personal });
    queryClient.invalidateQueries({ queryKey: dashboardKeys.myTicketsAll });
    queryClient.invalidateQueries({ queryKey: teamKeys.all });
    if (siteId != null) {
        queryClient.invalidateQueries({ queryKey: siteKeys.detail(String(siteId)) });
    }
    else if (invalidateAllSites) {
        queryClient.invalidateQueries({ queryKey: siteKeys.all });
    }
}
export function useTickets(cardId) {
    return useQuery({
        queryKey: ticketKeys.list(cardId),
        queryFn: () => tickets.list(cardId),
        enabled: !!cardId,
    });
}
export function useTicket(ticketId) {
    return useQuery({
        queryKey: ticketKeys.detail(ticketId),
        // A read answered after the session changed never reaches the cache; the
        // query's retry reads again in the new session.
        queryFn: async () => {
            const epoch = getSessionEpoch();
            const ticket = await tickets.get(ticketId);
            if (!isCurrentSessionEpoch(epoch))
                throw new StaleSessionError();
            return ticket;
        },
        enabled: !!ticketId,
        // A read never replaces a cached ticket with an older revision (#967).
        structuralSharing: (current, incoming) => keepNewerTicket(current, incoming),
    });
}
export function usePreviewTicketCoordinatesPush(ticketId) {
    return useMutation({
        mutationFn: () => tickets.previewCoordinatesPush(ticketId),
    });
}
export function usePushTicketCoordinatesToCard(ticketId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => tickets.pushCoordinatesToCard(ticketId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
            queryClient.invalidateQueries({ queryKey: siteKeys.all });
        },
    });
}
export function useTicketAuditTrail(ticketId, enabled = false) {
    return useQuery({
        queryKey: ticketKeys.auditTrail(ticketId),
        queryFn: () => tickets.getAuditTrail(ticketId),
        enabled: enabled && !!ticketId,
    });
}
export function useCreateTicket(cardId, options) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => tickets.create(cardId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ticketKeys.list(cardId) });
            invalidateTicketRollups(queryClient, options?.siteId);
        },
    });
}
export function useCopyTicket(ticketId, options) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => tickets.copyTicket(ticketId, data),
        onMutate: () => ({ epoch: getSessionEpoch() }),
        onSuccess: (copiedTicket, _data, context) => {
            if (!isCurrentSessionEpoch(context.epoch))
                return;
            setTicketDetail(queryClient, copiedTicket.id, copiedTicket, context.epoch);
            queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
            if (options?.sourceCardId) {
                queryClient.invalidateQueries({ queryKey: ticketKeys.list(options.sourceCardId) });
            }
            if (copiedTicket.card_id) {
                queryClient.invalidateQueries({ queryKey: ticketKeys.list(copiedTicket.card_id) });
            }
            invalidateTicketRollups(queryClient, options?.sourceSiteId);
            invalidateTicketRollups(queryClient, copiedTicket.site_id);
        },
    });
}
/**
 * The one way a ticket enters the detail cache outside its query (#967):
 * - never from an operation that started in an earlier session epoch
 *   (`epoch`, captured when the operation started): returns null and writes
 *   nothing;
 * - never over a cached ticket with a higher revision (keepNewerTicket).
 * Otherwise returns the ticket the cache holds afterwards.
 *
 * The single-ticket GET and the ticket PUT (so a bill-to change refreshes it,
 * #990) derive openinvoice_billing_required; other mutation responses lack
 * it, and "missing" is deliberately treated as strict. The
 * previous GET's value is carried forward, or every save would flash the
 * AFE/Requisitioner required state on non-OpenInvoice tickets until the next
 * refetch re-derives it.
 */
export function setTicketDetail(queryClient, ticketId, incoming, epoch) {
    if (!isCurrentSessionEpoch(epoch))
        return null;
    return queryClient.setQueryData(ticketKeys.detail(ticketId), (prev) => {
        const kept = keepNewerTicket(prev, incoming);
        if (kept !== incoming)
            return kept;
        return incoming.openinvoice_billing_required === undefined
            && prev?.openinvoice_billing_required !== undefined
            ? { ...incoming, openinvoice_billing_required: prev.openinvoice_billing_required }
            : incoming;
    });
}
export function useUpdateTicket(ticketId, cardId, options) {
    const queryClient = useQueryClient();
    // Track the latest scheduled delayed-invalidation handle so we can clear it
    // on unmount (or on a subsequent save). Without this, navigating away after
    // a save leaves a pending timer that fires a wasted refetch, and rapid
    // save+navigate cycles accumulate pending timers.
    const pendingInvalidationRef = useRef(null);
    useEffect(() => {
        return () => {
            if (pendingInvalidationRef.current !== null) {
                clearTimeout(pendingInvalidationRef.current);
                // Also clear from the module-level registry if this handle is the one
                // registered for the ticket (avoid leaving a stale entry pointing at
                // a cleared handle).
                if (pendingTrelloSyncInvalidations.get(ticketId) === pendingInvalidationRef.current) {
                    pendingTrelloSyncInvalidations.delete(ticketId);
                }
                pendingInvalidationRef.current = null;
            }
        };
    }, [ticketId]);
    // Ticket-scoped keys detach the observer on a switch: pending state and
    // per-call callbacks stay with the old visit, and its hook-level cache
    // callbacks keep the originating ticket's options. The other mutations
    // composed by useTicketForm follow this rule too (#972).
    return useMutation({
        mutationKey: [...ticketKeys.detail(ticketId), 'update'],
        mutationFn: ({ sessionEpoch, ...data }) => {
            const beforeSend = sessionEpoch === undefined ? undefined : () => {
                if (!isCurrentSessionEpoch(sessionEpoch))
                    throw new StaleSessionError();
            };
            beforeSend?.();
            const signal = options?.getAbortSignal?.();
            const current = queryClient.getQueryData(ticketKeys.detail(ticketId));
            const versionedData = data.base_revision !== undefined || data.base_updated_at
                || typeof current?.revision !== 'number'
                ? data
                : { ...data, base_revision: current.revision };
            if (options?.put)
                return options.put(versionedData, signal);
            if (beforeSend)
                return tickets.update(ticketId, versionedData, signal, beforeSend);
            // Keep the call shape minimal when there are no extra args so existing
            // callers/tests that assert the (id, data) signature stay valid.
            return signal
                ? tickets.update(ticketId, versionedData, signal)
                : tickets.update(ticketId, versionedData);
        },
        // The session epoch the save started in: an answer after a sign-out or
        // user switch touches no cache.
        onMutate: (variables) => ({ epoch: variables.sessionEpoch ?? getSessionEpoch() }),
        onSuccess: (updatedTicket, _data, context) => {
            if (!isCurrentSessionEpoch(context.epoch))
                return;
            // Update cache directly (no refetch flicker), keeping the GET-derived
            // OI gate scope that the PUT response does not carry.
            setTicketDetail(queryClient, ticketId, updatedTicket, context.epoch);
            // Still invalidate list to keep it in sync
            if (cardId) {
                queryClient.invalidateQueries({ queryKey: ticketKeys.list(cardId) });
            }
            invalidateTicketRollups(queryClient, updatedTicket.site_id);
            // Clear any previously-scheduled delayed invalidation for this ticket so
            // only the latest save's timer can fire (prevents stacking from rapid
            // consecutive saves).
            cancelPendingTrelloSyncInvalidation(ticketId);
            // Trello sync runs server-side with a ~1s debounce + retries. Schedule a
            // delayed refetch so the frontend can pick up trello_sync_failed if the
            // async sync fails after the mutation response has already returned.
            const handle = setTimeout(() => {
                pendingTrelloSyncInvalidations.delete(ticketId);
                if (pendingInvalidationRef.current === handle) {
                    pendingInvalidationRef.current = null;
                }
                queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
            }, TRELLO_SYNC_FAILURE_POLL_MS);
            pendingInvalidationRef.current = handle;
            pendingTrelloSyncInvalidations.set(ticketId, handle);
        },
    });
}
export function useTicketQboSyncCandidates(enabled = true) {
    return useQuery({
        queryKey: ticketKeys.qboSyncCandidates,
        queryFn: () => tickets.listQboSyncCandidates(),
        enabled,
        staleTime: 15_000,
    });
}
export function usePreviewTicketQboSync() {
    return useMutation({ mutationFn: tickets.previewQboSync });
}
export function useExecuteTicketQboSync(ticketId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: tickets.executeQboSync,
        onSuccess: async (result) => {
            await queryClient.invalidateQueries({ queryKey: ticketKeys.all });
            if (result.operation?.id) {
                queryClient.setQueryData(ticketKeys.qboSyncOperation(result.operation.id), result.operation);
            }
            await queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
        },
    });
}
export function useReconcileTicketQboSync(ticketId, operationId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => tickets.reconcileQboSync(operationId, data),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ticketKeys.all });
            await queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
        },
    });
}
export function useCleanupTicketDescription() {
    return useMutation({
        mutationFn: (description) => tickets.cleanupDescription({ description }),
    });
}
export function useTransitionTicket(ticketId, cardId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [...ticketKeys.detail(ticketId), 'transition'],
        mutationFn: ({ sessionEpoch, ...payload }) => sessionEpoch === undefined
            ? tickets.transition(ticketId, payload)
            : tickets.transition(ticketId, payload, undefined, () => {
                if (!isCurrentSessionEpoch(sessionEpoch))
                    throw new StaleSessionError();
            }),
        onMutate: (variables) => ({ epoch: variables.sessionEpoch ?? getSessionEpoch() }),
        onSuccess: (updatedTicket, _payload, context) => {
            if (!isCurrentSessionEpoch(context.epoch))
                return;
            // Update cache directly (no refetch flicker), keeping the GET-derived
            // OI gate scope that the transition response does not carry.
            setTicketDetail(queryClient, ticketId, updatedTicket, context.epoch);
            // Still invalidate list to keep it in sync
            if (cardId) {
                queryClient.invalidateQueries({ queryKey: ticketKeys.list(cardId) });
            }
            invalidateTicketRollups(queryClient, updatedTicket.site_id);
            // A status transition changes review-queue membership (e.g. field_review →
            // ready_for_invoice leaves the queue), so refresh it. This is what lets the
            // desktop review pane auto-advance to the next ticket after an approve, and
            // keeps the mobile review modal's queue current too.
            queryClient.invalidateQueries({ queryKey: reviewKeys.queue });
        },
    });
}
/** Restore an archived ticket to its server-resolved pre-archive status. */
export function useUnarchiveTicket(ticketId, cardId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [...ticketKeys.detail(ticketId), 'unarchive'],
        mutationFn: () => tickets.unarchive(ticketId),
        onMutate: () => ({ epoch: getSessionEpoch() }),
        onSuccess: (result, _v, context) => {
            if (!isCurrentSessionEpoch(context.epoch))
                return;
            setTicketDetail(queryClient, ticketId, result.ticket, context.epoch);
            if (cardId) {
                queryClient.invalidateQueries({ queryKey: ticketKeys.list(cardId) });
            }
            queryClient.invalidateQueries({ queryKey: reviewKeys.queue });
            invalidateTicketRollups(queryClient, result.ticket.site_id, true);
        },
    });
}
/**
 * Send ready-for-invoice tickets to QBO as one grouped document (estimate or
 * invoice per the admin toggle). On success the tickets become invoiced + locked,
 * so refresh the detail, the card list, active lists, and the review queue.
 */
export function useSendToQbo(ticketId, cardId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [...ticketKeys.detail(ticketId), 'send-to-qbo'],
        mutationFn: (ticketIds) => tickets.sendToQbo(ticketIds),
        // An answer after a sign-out or user switch refreshes nothing for the
        // next session.
        onMutate: () => ({ epoch: getSessionEpoch() }),
        onSuccess: (_result, _ticketIds, context) => {
            if (!isCurrentSessionEpoch(context.epoch))
                return;
            queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
            if (cardId) {
                queryClient.invalidateQueries({ queryKey: ticketKeys.list(cardId) });
            }
            queryClient.invalidateQueries({ queryKey: ticketKeys.active });
            queryClient.invalidateQueries({ queryKey: reviewKeys.queue });
            invalidateTicketRollups(queryClient, null, true);
        },
    });
}
/**
 * Send-to-QBO for the review queue: groups any number of selected ready tickets
 * into one estimate/invoice. Not tied to a single ticket/card, so it only
 * invalidates the active + review-queue lists.
 */
export function useSendToQboBatch() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ ticketIds, consolidate, billToCustomerId, customGroups, docType, primaryTicketId, headerSelections }) => tickets.sendToQbo(ticketIds, consolidate, billToCustomerId, customGroups, docType, primaryTicketId, headerSelections),
        onSuccess: (_data, variables) => {
            // Each pushed ticket is now invoiced + locked — refresh any open detail
            // view (e.g. the review pane showing one of them) along with the lists.
            variables.ticketIds.forEach((ticketId) => {
                queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
            });
            queryClient.invalidateQueries({ queryKey: ticketKeys.active });
            queryClient.invalidateQueries({ queryKey: reviewKeys.queue });
            invalidateTicketRollups(queryClient, null, true);
            // The backend just saved this customer's last-used shape; drop the cached
            // prefs so the next Import popup refetches the value it will prefill from.
            if (variables?.billToCustomerId) {
                queryClient.invalidateQueries({ queryKey: consolidationPrefsKey(variables.billToCustomerId) });
            }
        },
    });
}
/**
 * Mark selected tickets invoiced with an external/manual invoice number. Like
 * QBO push, the tickets move out of active billing work and the review queue.
 */
export function useBulkMarkTicketsInvoiced() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ ticketIds, invoiceNumber, allowShared }) => tickets.bulkMarkInvoiced(ticketIds, invoiceNumber, allowShared),
        onSuccess: (_data, variables) => {
            variables.ticketIds.forEach((ticketId) => {
                queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
            });
            queryClient.invalidateQueries({ queryKey: ticketKeys.active });
            queryClient.invalidateQueries({ queryKey: reviewKeys.queue });
            invalidateTicketRollups(queryClient, null, true);
        },
    });
}
/** Query key for a customer's last-used consolidation prefs. */
export function consolidationPrefsKey(customerId) {
    return ['consolidation-prefs', customerId];
}
/**
 * Dry-run draft of the grouped QBO document for the Import popup preview.
 * Keyed on the ticket ids + the full consolidation shape + custom groups, so
 * flipping a toggle refetches while flipping it back serves the cached draft.
 * staleTime 0: ticket data can change under an open modal; a reopen refetches.
 *
 * ticketIds keep their given order — customGroups line keys are positional per
 * day section, so the draft MUST use the same ticket order the push will.
 */
export function useQboImportDraft(ticketIds, consolidate, enabled, customGroups = [], docType, 
// #744: primary ticket (default first) + the human's header picks. Changing
// either refetches so the preview + candidate pickers reflect the exact header.
primaryTicketId, headerSelections) {
    return useQuery({
        queryKey: [
            'qbo-import-draft',
            ticketIds,
            consolidate.labor,
            consolidate.materialMode,
            consolidate.material,
            consolidate.equipment,
            consolidate.laborIncludePm,
            consolidate.laborIncludeTruck,
            customGroups,
            // Flipping the estimate toggle refetches the preview so it reflects the
            // doc type the push will create.
            docType ?? 'default',
            primaryTicketId ?? 'primary-default',
            headerSelections ?? 'no-selections',
        ],
        queryFn: () => tickets.draftQboImport(ticketIds, consolidate, customGroups.length > 0 ? customGroups : undefined, docType, primaryTicketId, headerSelections),
        enabled: enabled && ticketIds.length > 0,
        staleTime: 0,
    });
}
/**
 * Last-used consolidation shape for a bill-to customer, used to prefill the
 * Import popup. Disabled until a customerId is known (nothing selected yet).
 */
export function useConsolidationPrefs(customerId) {
    return useQuery({
        queryKey: consolidationPrefsKey(customerId),
        queryFn: () => tickets.getConsolidationPrefs(customerId),
        enabled: Boolean(customerId),
        staleTime: 0,
    });
}
/**
 * Recall (manager/admin) an invoiced ticket: void its QBO invoice and revert it
 * to 'ready_for_invoice'. The ticket leaves the terminal invoiced state and
 * re-enters the active/review surfaces, so invalidate the same set as a push.
 */
export function useRecallTicket(ticketId, cardId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars) => tickets.recall(ticketId, vars?.reason, vars?.recallGroup),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
            if (cardId) {
                queryClient.invalidateQueries({ queryKey: ticketKeys.list(cardId) });
            }
            queryClient.invalidateQueries({ queryKey: ticketKeys.active });
            queryClient.invalidateQueries({ queryKey: reviewKeys.queue });
            invalidateTicketRollups(queryClient, null, true);
        },
    });
}
/**
 * Email a ticket-linked QBO estimate to the customer. On success the group's
 * tickets carry `estimate_sent_at` (chip flips to "Estimate sent"), so refresh
 * the detail view plus the active/review surfaces where the chip renders.
 * Optional `ticketIds` lets a grouped estimate refresh every member's detail.
 */
export function useSendEstimate(qbEstimateId, ticketId, ticketIds) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars) => tickets.sendEstimate(qbEstimateId, vars?.email),
        onSuccess: () => {
            if (ticketId) {
                queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
            }
            (ticketIds ?? []).forEach((id) => {
                queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
            });
            queryClient.invalidateQueries({ queryKey: ticketKeys.active });
            queryClient.invalidateQueries({ queryKey: reviewKeys.queue });
            invalidateTicketRollups(queryClient, null, true);
        },
    });
}
/**
 * One-click convert a ticket-linked estimate into a new linked QBO invoice. The
 * whole estimated group moves to 'invoiced' and leaves the active surfaces, so
 * invalidate the same set as a push/recall. Error codes surface via onError at
 * the call site (already_converted / validation_failed / missing_in_qbo).
 */
export function useConvertEstimate(qbEstimateId, ticketQboDocumentId, ticketId, ticketIds) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars) => tickets.convertEstimate(qbEstimateId, ticketQboDocumentId, vars?.customerPoId),
        onSuccess: (result) => {
            if (ticketId) {
                queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
            }
            const ids = result.ticketIds?.length ? result.ticketIds : (ticketIds ?? []);
            ids.forEach((id) => {
                queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
            });
            queryClient.invalidateQueries({ queryKey: ticketKeys.active });
            queryClient.invalidateQueries({ queryKey: reviewKeys.queue });
            invalidateTicketRollups(queryClient, null, true);
        },
    });
}
export function useMarkTicketInvoiced(ticketId, cardId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => tickets.markInvoiced(ticketId, data),
        onMutate: () => ({ epoch: getSessionEpoch() }),
        onSuccess: (result, _data, context) => {
            if (!isCurrentSessionEpoch(context.epoch))
                return;
            if (result.ticket) {
                setTicketDetail(queryClient, ticketId, result.ticket, context.epoch);
            }
            else {
                queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
            }
            if (cardId) {
                queryClient.invalidateQueries({ queryKey: ticketKeys.list(cardId) });
            }
            queryClient.invalidateQueries({ queryKey: ticketKeys.active });
            queryClient.invalidateQueries({ queryKey: reviewKeys.queue });
            invalidateTicketRollups(queryClient, null, true);
        },
    });
}
export function useTicketAttachments(ticketId, options = {}) {
    const limit = options.limit ?? 50;
    const offset = options.offset ?? 0;
    return useQuery({
        queryKey: [...ticketKeys.attachments(ticketId), { limit, offset }],
        queryFn: () => tickets.getAttachments(ticketId, { limit, offset }),
        enabled: !!ticketId,
    });
}
export function useUploadTicketAttachment(ticketId, siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [...ticketKeys.detail(ticketId), 'upload-attachment'],
        mutationFn: (formData) => tickets.uploadAttachment(ticketId, formData),
        onSuccess: (newAttachment) => {
            // Invalidate any paginated cache entries (we don't know which page).
            queryClient.invalidateQueries({ queryKey: ticketKeys.attachments(ticketId) });
            // Optimistically prepend to a first-page cache hit if present.
            queryClient.setQueriesData({ queryKey: ticketKeys.attachments(ticketId) }, (old) => {
                if (!old)
                    return old;
                return {
                    ...old,
                    attachments: [newAttachment, ...old.attachments],
                    total: old.total + 1,
                };
            });
            const resolvedSiteId = siteId || newAttachment.site_id;
            if (resolvedSiteId) {
                queryClient.invalidateQueries({ queryKey: siteKeys.attachments(resolvedSiteId) });
                queryClient.invalidateQueries({ queryKey: siteKeys.detail(resolvedSiteId) });
            }
            queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
            queryClient.invalidateQueries({ queryKey: ticketKeys.active });
        },
    });
}
export function useDeleteTicketAttachment(ticketId, siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [...ticketKeys.detail(ticketId), 'delete-attachment'],
        mutationFn: (attachmentId) => tickets.deleteAttachment(attachmentId),
        onSuccess: (_data, attachmentId) => {
            queryClient.setQueriesData({ queryKey: ticketKeys.attachments(ticketId) }, (old) => {
                if (!old)
                    return old;
                const remaining = old.attachments.filter((a) => a.id !== attachmentId);
                return {
                    ...old,
                    attachments: remaining,
                    total: Math.max(0, old.total - (old.attachments.length - remaining.length)),
                };
            });
            if (siteId) {
                queryClient.invalidateQueries({ queryKey: siteKeys.attachments(siteId) });
            }
            queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
            queryClient.invalidateQueries({ queryKey: ticketKeys.active });
        },
    });
}
export function useUpdateTicketAttachment(ticketId, siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [...ticketKeys.detail(ticketId), 'update-attachment'],
        mutationFn: ({ attachmentId, attachmentType, }) => tickets.updateAttachment(attachmentId, { attachment_type: attachmentType }),
        onMutate: async ({ attachmentId, attachmentType }) => {
            await queryClient.cancelQueries({ queryKey: ticketKeys.attachments(ticketId) });
            const previous = queryClient.getQueriesData({
                queryKey: ticketKeys.attachments(ticketId),
            });
            queryClient.setQueriesData({ queryKey: ticketKeys.attachments(ticketId) }, (old) => {
                if (!old)
                    return old;
                return {
                    ...old,
                    attachments: old.attachments.map((attachment) => attachment.id === attachmentId
                        ? { ...attachment, attachment_type: attachmentType }
                        : attachment),
                };
            });
            return { previous };
        },
        onError: (_err, _variables, context) => {
            context?.previous?.forEach(([key, data]) => {
                queryClient.setQueryData(key, data);
            });
        },
        onSuccess: (updatedAttachment) => {
            queryClient.setQueriesData({ queryKey: ticketKeys.attachments(ticketId) }, (old) => {
                if (!old)
                    return old;
                return {
                    ...old,
                    attachments: old.attachments.map((attachment) => attachment.id === updatedAttachment.id ? updatedAttachment : attachment),
                };
            });
            if (siteId || updatedAttachment.site_id) {
                queryClient.invalidateQueries({ queryKey: siteKeys.attachments(siteId || updatedAttachment.site_id) });
            }
            queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
        },
    });
}
export function useDeleteTicket(ticketId, cardId, siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [...ticketKeys.detail(ticketId), 'delete'],
        mutationFn: () => tickets.deleteTicket(ticketId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
            if (cardId) {
                queryClient.invalidateQueries({ queryKey: ticketKeys.list(cardId) });
            }
            invalidateTicketRollups(queryClient, siteId);
            queryClient.invalidateQueries({ queryKey: reviewKeys.queue });
        },
    });
}
export function usePermanentDeleteTicket(ticketId, cardId, siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => tickets.permanentDelete(ticketId),
        onSuccess: () => {
            queryClient.removeQueries({ queryKey: ticketKeys.detail(ticketId) });
            if (cardId) {
                queryClient.invalidateQueries({ queryKey: ticketKeys.list(cardId) });
            }
            invalidateTicketRollups(queryClient, siteId, true);
            queryClient.invalidateQueries({ queryKey: reviewKeys.queue });
        },
    });
}
export function useRefreshPaidStatus(ticketId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => tickets.refreshPaidStatus(ticketId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
            queryClient.invalidateQueries({ queryKey: ticketKeys.active });
        },
    });
}
export function useUpdateIntermediarySubmission(ticketId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => tickets.updateIntermediarySubmission(ticketId, data),
        onMutate: () => ({ epoch: getSessionEpoch() }),
        onSuccess: (result, _data, context) => {
            if (!isCurrentSessionEpoch(context.epoch))
                return;
            if (result.ticket) {
                setTicketDetail(queryClient, ticketId, result.ticket, context.epoch);
            }
            else {
                queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
            }
            queryClient.invalidateQueries({ queryKey: ticketKeys.active });
            queryClient.invalidateQueries({ queryKey: reviewKeys.queue });
        },
    });
}
export function useSyncTrello(ticketId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => tickets.syncTrello(ticketId),
        onMutate: () => {
            // Cancel any pending delayed invalidation scheduled by a prior save for
            // this ticket. Otherwise that stale timer can fire after the retry
            // succeeds and briefly overwrite fresh state (e.g. flashing
            // trello_sync_failed back on after it was cleared).
            cancelPendingTrelloSyncInvalidation(ticketId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
        },
    });
}
// ============================================
// Ticket Comments
// ============================================
export function useTicketComments(ticketId) {
    return useQuery({
        queryKey: ticketKeys.comments(ticketId),
        queryFn: () => tickets.listComments(ticketId),
        enabled: !!ticketId,
        // ticket_comments isn't in the realtime publication; polling is the deliberate freshness parity choice.
        refetchInterval: 30_000,
    });
}
export function useCreateComment() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ ticketId, body }) => tickets.createComment(ticketId, body),
        onSuccess: (newComment, { ticketId }) => {
            queryClient.setQueryData(ticketKeys.comments(ticketId), (old = []) => [...old, newComment]);
            // Bump ticket detail so comment_count refreshes if the detail endpoint
            // starts populating it.
            queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
            queryClient.invalidateQueries({ queryKey: ticketKeys.active });
        },
    });
}
export function useUpdateTicketComment() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ ticketId, commentId, body }) => tickets.updateComment(ticketId, commentId, body),
        onSuccess: (updated, { ticketId }) => {
            queryClient.setQueryData(ticketKeys.comments(ticketId), (old = []) => old.map((c) => (c.id === updated.id ? updated : c)));
        },
    });
}
export function useDeleteComment(defaultTicketId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars) => {
            if (typeof vars === 'string') {
                if (!defaultTicketId) {
                    return Promise.reject(new Error('useDeleteComment: pass { ticketId, commentId } or call useDeleteComment(ticketId) first'));
                }
                return tickets.deleteComment(defaultTicketId, vars);
            }
            return tickets.deleteComment(vars.ticketId, vars.commentId);
        },
        onSuccess: (_data, vars) => {
            const commentId = typeof vars === 'string' ? vars : vars.commentId;
            const ticketId = typeof vars === 'string' ? defaultTicketId : vars.ticketId;
            if (ticketId) {
                queryClient.setQueryData(ticketKeys.comments(ticketId), (old = []) => old.filter((c) => c.id !== commentId));
                queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) });
                queryClient.invalidateQueries({ queryKey: ticketKeys.active });
            }
            else {
                // Fallback: no ticket context — nuke the whole tickets tree so every
                // open comments query refetches.
                queryClient.invalidateQueries({ queryKey: ticketKeys.all });
            }
        },
    });
}
//# sourceMappingURL=useTickets.js.map